import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import { useState } from 'react';

import Navbar from './navigation/Navbar';
import Footer from './navigation/Footer';
import Toasty from './components/Toasty';
import ScrollToTop from './components/ScrollToTop';

// Context Providers
import { UserProvider } from './contexts/UserContext';
import { CommunityProvider } from './contexts/CommunityContext';

// Views
import Home from './views/Home';
import Country from './views/Country';
import Directory from './views/Directory';
import Review from './views/Review';
import Hotel from './views/Hotel';
import Restaurant from './views/Restaurant';
import Experience from './views/Experience';
import Event from './views/Event';
import Article from './views/Article';
import Blog from './views/Blog';
import Destinations from './views/Destinations';
import Community from './views/Community';
import ProfilePage from './views/ProfilePage';
import Construction from './views/Construction';
import QueerLandscapePage from './views/QueerLandscape';
import CountryProfile from './views/CountryProfile';
import OurStory from './views/OurStory';
import SitePage from './views/SitePage';
import TravelGuide from './views/TravelGuide';
import Beach from './views/Beach';
import ExploreDetail from './views/ExploreDetail';
import WellnessDetail from './views/WellnessDetail';
import PlayDetail from './views/PlayDetail';
import ShopLocalDetail from './views/ShopLocalDetail';
import ConnectivityTool from './views/ConnectivityTool';
import ConnectAuth from './views/connect/ConnectAuth';
import ConnectProfile from './views/connect/ConnectProfile';
import ConnectMessages from './views/connect/ConnectMessages';
import ConnectDiscover from './views/connect/ConnectDiscover';
import ConnectUserProfile from './views/connect/ConnectUserProfile';
import ConnectVerifyEmail from './views/connect/ConnectVerifyEmail';
import ConnectMeet from './views/connect/ConnectMeet';

// Dashboard
import Dashboard from './views/dashboard/Dashboard';
import Login from './views/dashboard/Login';
import Countries from './views/dashboard/modules/Countries';
import Hotels from './views/dashboard/modules/Hotels';
import Restaurants from './views/dashboard/modules/Restaurants';
import Experiences from './views/dashboard/modules/Experiences';
import Events from './views/dashboard/modules/Events';
import QueerLandscapeDashboard from './views/dashboard/modules/QueerLandscape';
import Articles from './views/dashboard/modules/Articles';
import SitePages from './views/dashboard/modules/SitePages';
import CountryProfiles from './views/dashboard/modules/CountryProfiles';
import TravelGuides from './views/dashboard/modules/TravelGuides';
import BeachesDashboard from './views/dashboard/modules/Beaches';
import ExploreDashboard from './views/dashboard/modules/Explore';
import WellnessDashboard from './views/dashboard/modules/Wellness';
import PlayDashboard from './views/dashboard/modules/Play';
import ShopLocalDashboard from './views/dashboard/modules/ShopLocal';

import './App.css';
import './styles/Responsive.css';
import AccountCreation from './views/community/AccountCreation';
import Account from './views/community/Account';
import ProtectedRoute from './components/ProtectedRoute';

// Navbar that hides on dashboard and connect routes
function RouteAwareNavbar() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isConnect = location.pathname.startsWith('/connect');
  return (isDashboard || isConnect) ? null : <Navbar />;
}

// Footer that hides on dashboard and connect routes
function RouteAwareFooter() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isConnect = location.pathname.startsWith('/connect');
  return (isDashboard || isConnect) ? null : <Footer />;
}
function App() {

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSuccess, setToastSuccess] = useState(false);

  const triggerToast = (message, success) => {
    setToastMessage(message);
    setToastSuccess(success);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000); // Toast disappears after 3 seconds
  };



  return (
    <CookiesProvider>
      <UserProvider>
        <CommunityProvider>
          <Router>
            <ScrollToTop />
            <RouteAwareNavbar />
            <Toasty showToast={showToast} message={toastMessage} toastSuccess={toastSuccess}/>
            <div className='app-content'>
            <Routes>
              <Route path='/' element={<Home/>} />
              {/* Country detail by slug for prettier URLs, keep legacy id-based route too */}
              <Route path='/country/:slug' element={<Country/>} />
              <Route path='/destinations' element={<Destinations/>} />
              <Route path='/country' element={<Country/>} />
              <Route path='/directory' element={<Directory/>} />
              <Route path='/review' element={<Review/>} />
              <Route path='/hotel/:slug' element={<Hotel/>} />
              <Route path='/hotel' element={<Hotel/>} />
              <Route path='/restaurant/:slug' element={<Restaurant/>} />
              <Route path='/restaurant' element={<Restaurant/>} />
              <Route path='/experience/:slug' element={<Experience/>} />
              <Route path='/experience' element={<Experience/>} />
              <Route path='/event/:slug' element={<Event/>} />
              <Route path='/event' element={<Event/>} />
              <Route path='/article/:slug' element={<Blog/>} />
              <Route path='/article' element={<Blog/>} />
              <Route path='/blog' element={<Blog/>} />
              <Route path='/queer-landscape/:countryId' element={<QueerLandscapePage/>} />
              <Route path='/country-profile/:countryId' element={<CountryProfile />} />
              <Route path='/travel-guide/:countryId' element={<TravelGuide />} />
              <Route path='/beach/:slug' element={<Beach/>} />
              <Route path='/beach' element={<Beach/>} />
              <Route path='/explore/:slug' element={<ExploreDetail/>} />
              <Route path='/explore' element={<ExploreDetail/>} />
              <Route path='/wellness/:slug' element={<WellnessDetail/>} />
              <Route path='/wellness' element={<WellnessDetail/>} />
              <Route path='/play/:slug' element={<PlayDetail/>} />
              <Route path='/play' element={<PlayDetail/>} />
              <Route path='/shop/:slug' element={<ShopLocalDetail/>} />
              <Route path='/shop' element={<ShopLocalDetail/>} />
              <Route path='/our-story' element={<OurStory/>} />
              <Route path='/about-us' element={<SitePage slug='about-us'/>} />
              <Route path='/connectivity-tool' element={<ConnectivityTool/>} />
              
              {/* Community App Routes */}
              <Route path='/connect' element={<ConnectAuth/>} />
              <Route path='/connect/profile' element={<ConnectProfile/>} />
              <Route path='/connect/messages' element={<ConnectMessages/>} />
              <Route path='/connect/discover' element={<ConnectDiscover/>} />
              <Route path='/connect/meet' element={<ConnectMeet/>} />
              <Route path='/connect/user/:id' element={<ConnectUserProfile/>} />
              <Route path='/connect/verify-email' element={<ConnectVerifyEmail/>} />
              <Route path='/community' element={<Community/>} />
              <Route path="/community/account-creation" element={<AccountCreation/>} />
              <Route path="/community/account" element={<Account/>} />
              <Route path='/profiles' element={<ProfilePage/>} />
              <Route path='/profiles/:profileId' element={<ProfilePage/>} />

              {/* Dashboard Login */}
              <Route path='/dashboard/login' element={<Login />} />

              {/* Dashboard Routes (Protected) */}
              <Route path='/dashboard' element={<ProtectedRoute><Dashboard /></ProtectedRoute>}>
                <Route index element={<Countries />} />
                <Route path='countries' element={<Countries />} />
                <Route path='hotels' element={<Hotels />} />
                <Route path='restaurants' element={<Restaurants />} />
                {/* <Route path='experiences' element={<Experiences />} /> */}
                <Route path='events' element={<Events />} />
                <Route path='queer-landscape' element={<QueerLandscapeDashboard />} />
                <Route path='articles' element={<Articles />} />
                <Route path='site-pages' element={<SitePages />} />
                <Route path='country-profiles' element={<CountryProfiles />} />
                <Route path='travel-guides' element={<TravelGuides />} />
                <Route path='beaches' element={<BeachesDashboard />} />
                <Route path='explore' element={<ExploreDashboard />} />
                <Route path='wellness' element={<WellnessDashboard />} />
                <Route path='play' element={<PlayDashboard />} />
                <Route path='shop-local' element={<ShopLocalDashboard />} />
              </Route>
              
              
              {/* <Route path="/" element={<Construction triggerToast={triggerToast}/>} /> */}
            </Routes>
            </div>
            <RouteAwareFooter />
          </Router>
        </CommunityProvider>
      </UserProvider>
    </CookiesProvider>
  );
}

export default App;
